import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Phone, PhoneIncoming, PhoneOutgoing, AlertTriangle, CheckCircle, Frown, RefreshCw, Sparkles, Search, Plus, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

export default function CallLogPage({ API_BASE }) {
  const [calls, setCalls]       = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd]   = useState(false);
  const [filters, setFilters]   = useState({ direction: '', sentiment: '', missed: false, q: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.direction) params.direction = filters.direction;
      if (filters.sentiment) params.sentiment = filters.sentiment;
      if (filters.missed) params.missed = '1';
      const [a, b] = await Promise.all([
        axios.get(`${API_BASE}/calls`, { params }),
        axios.get(`${API_BASE}/calls/stats`),
      ]);
      let list = a.data || [];
      if (filters.q) {
        const q = filters.q.toLowerCase();
        list = list.filter(c => (c.fromNumber||'').toLowerCase().includes(q) || (c.summary||'').toLowerCase().includes(q) || (c.patientId?.name||'').toLowerCase().includes(q));
      }
      setCalls(list);
      setStats(b.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API_BASE, filters]);

  useEffect(() => { load(); }, [load]);

  const sentimentColor = (s) => s === 'positive' ? theme.success : s === 'negative' ? theme.danger : theme.textMuted;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>📞 Call Log & AI Intelligence</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btn(theme.secondary)}><RefreshCw size={14}/> Reload</button>
          <button onClick={() => setShowAdd(true)} style={btn(theme.primary)}><Plus size={14}/> Manual Entry</button>
        </div>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 18 }}>
          <Stat label="Total Calls (30d)" value={stats.total} color={theme.navy}/>
          <Stat label="Inbound" value={stats.inbound} color={theme.info}/>
          <Stat label="Booked" value={stats.booked} color={theme.success}/>
          <Stat label="Missed Booking" value={stats.missedBooking} color={theme.danger}/>
          <Stat label="Unhappy Callers" value={stats.unhappy} color={theme.warning}/>
          <Stat label="Avg Staff Score" value={stats.avgStaffScore + '%'} color={theme.purple}/>
        </div>
      )}

      <div style={{ ...cardStyle, padding: 14, marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select value={filters.direction} onChange={e=>setFilters({...filters, direction:e.target.value})} style={{ ...inputStyle, width: 140 }}>
          <option value="">All Directions</option><option value="inbound">Inbound</option><option value="outbound">Outbound</option>
        </select>
        <select value={filters.sentiment} onChange={e=>setFilters({...filters, sentiment:e.target.value})} style={{ ...inputStyle, width: 140 }}>
          <option value="">All Sentiments</option><option value="positive">Positive</option><option value="neutral">Neutral</option><option value="negative">Negative</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={filters.missed} onChange={e=>setFilters({...filters, missed:e.target.checked})}/>
          Missed bookings only
        </label>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 12, color: theme.textMuted }}/>
          <input placeholder="Search number, patient, summary..." value={filters.q} onChange={e=>setFilters({...filters, q:e.target.value})} style={{ ...inputStyle, paddingLeft: 32, width: '100%' }}/>
        </div>
      </div>

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Loading...</div> : (
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14 }}>
        <div style={{ ...cardStyle, padding: 0, maxHeight: '70vh', overflowY: 'auto' }}>
          {calls.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
              <Phone size={32} style={{ opacity: 0.4 }}/><div style={{ marginTop: 8 }}>Koi call log nahi hai. Webhook configure karo ya manually entry add karo.</div>
            </div>
          ) : calls.map(c => (
            <div key={c._id} onClick={()=>setSelected(c)} style={{ padding: 14, borderBottom: `1px solid ${theme.borderLight}`, cursor: 'pointer', background: selected?._id === c._id ? theme.primaryLight : 'transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.direction === 'inbound' ? <PhoneIncoming size={16} color={theme.info}/> : <PhoneOutgoing size={16} color={theme.success}/>}
                  <strong style={{ fontSize: 14 }}>{c.patientId?.name || c.fromNumber || 'Unknown'}</strong>
                  {c.missedBookingFlag && <span style={pill(theme.danger)}><AlertTriangle size={11}/> Missed</span>}
                  {c.unhappyFlag && <span style={pill(theme.warning)}><Frown size={11}/> Unhappy</span>}
                  {c.outcome === 'booked' && <span style={pill(theme.success)}><CheckCircle size={11}/> Booked</span>}
                </div>
                <small style={{ color: theme.textMuted }}>{new Date(c.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</small>
              </div>
              {c.summary && <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>{c.summary.slice(0, 120)}{c.summary.length > 120 ? '…' : ''}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 6, fontSize: 11, color: theme.textMuted }}>
                <span>📞 {c.fromNumber || '?'}</span>
                {c.source && <span>· {c.source}</span>}
                {c.durationSec ? <span>· {Math.round(c.durationSec/60)}m</span> : null}
                <span style={{ color: sentimentColor(c.sentiment) }}>· {c.sentiment}</span>
              </div>
            </div>
          ))}
        </div>

        {selected && <CallDetail call={selected} API_BASE={API_BASE} onClose={()=>setSelected(null)} onChange={load}/>}
      </div>
      )}

      {showAdd && <AddCallModal API_BASE={API_BASE} onClose={()=>setShowAdd(false)} onSaved={()=>{setShowAdd(false); load();}}/>}
    </div>
  );
}

function CallDetail({ call, API_BASE, onClose, onChange }) {
  const [transcript, setTranscript] = useState(call.transcript || '');
  const [busy, setBusy] = useState(false);

  const analyze = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/calls/${call._id}/transcript`, { transcript });
      onChange();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  return (
    <div style={{ ...cardStyle, padding: 18, position: 'sticky', top: 14, height: 'fit-content', maxHeight: '70vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0, color: theme.navy }}>Call Detail</h3>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={18}/></button>
      </div>
      <div style={{ marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>
        <div><strong>From:</strong> {call.fromNumber}</div>
        <div><strong>To:</strong> {call.toNumber}</div>
        <div><strong>When:</strong> {new Date(call.createdAt).toLocaleString('en-IN')}</div>
        {call.patientId && <div><strong>Patient:</strong> {call.patientId.name} ({call.patientId.uhid})</div>}
        {call.source && <div><strong>Source:</strong> {call.source} {call.campaign && `· ${call.campaign}`}</div>}
        {call.durationSec ? <div><strong>Duration:</strong> {Math.round(call.durationSec/60)}m {call.durationSec%60}s</div> : null}
      </div>
      {call.recordingUrl && (
        <div style={{ marginTop: 10 }}>
          <audio controls src={call.recordingUrl} style={{ width: '100%' }}/>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <label style={labelStyle}>Transcript</label>
        <textarea rows={6} value={transcript} onChange={e=>setTranscript(e.target.value)} placeholder="Call ka transcript paste karo (Hindi/English chalega), AI analysis run karega..." style={{ ...inputStyle, width: '100%', resize: 'vertical' }}/>
        <button onClick={analyze} disabled={busy || !transcript} style={{ ...btn(theme.primary), marginTop: 8, width: '100%' }}>
          <Sparkles size={14}/> {busy ? 'Analyzing...' : 'Run AI Analysis'}
        </button>
      </div>

      {call.summary && (
        <div style={{ marginTop: 14, padding: 12, background: theme.primaryLight, borderRadius: theme.radiusSm }}>
          <div style={{ fontWeight: 700, color: theme.navy, marginBottom: 6 }}>🤖 AI Summary</div>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>{call.summary}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={pill(theme.info)}>{call.intent}</span>
            <span style={pill(call.sentiment === 'positive' ? theme.success : call.sentiment === 'negative' ? theme.danger : theme.textMuted)}>{call.sentiment}</span>
            <span style={pill(theme.purple)}>Score: {call.staffScore}/100</span>
          </div>
          {call.staffNotes && <div style={{ fontSize: 12, marginTop: 8, fontStyle: 'italic', color: theme.textMuted }}><strong>Staff feedback:</strong> {call.staffNotes}</div>}
        </div>
      )}
    </div>
  );
}

function AddCallModal({ API_BASE, onClose, onSaved }) {
  const [f, setF] = useState({ direction: 'inbound', fromNumber: '', toNumber: '', durationSec: 0, transcript: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/calls`, f);
      if (f.transcript) {
        await axios.post(`${API_BASE}/calls/${r.data._id}/transcript`, { transcript: f.transcript });
      }
      onSaved();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };
  return (
    <div style={modalOverlay}>
      <div style={{ ...cardStyle, padding: 24, width: 'min(520px, 92vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>New Call Entry</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Direction</label>
          <select value={f.direction} onChange={e=>setF({...f, direction:e.target.value})} style={inputStyle}>
            <option value="inbound">Inbound</option><option value="outbound">Outbound</option>
          </select>
          <label style={labelStyle}>From Number</label>
          <input value={f.fromNumber} onChange={e=>setF({...f, fromNumber:e.target.value})} style={inputStyle}/>
          <label style={labelStyle}>To Number</label>
          <input value={f.toNumber} onChange={e=>setF({...f, toNumber:e.target.value})} style={inputStyle}/>
          <label style={labelStyle}>Duration (seconds)</label>
          <input type="number" value={f.durationSec} onChange={e=>setF({...f, durationSec:Number(e.target.value)})} style={inputStyle}/>
          <label style={labelStyle}>Transcript (optional — AI analyze automatically)</label>
          <textarea rows={4} value={f.transcript} onChange={e=>setF({...f, transcript:e.target.value})} style={{ ...inputStyle, resize: 'vertical' }}/>
        </div>
        <button onClick={save} disabled={busy} style={{ ...btn(theme.primary), marginTop: 14, width: '100%' }}>{busy ? 'Saving...' : 'Save Call Entry'}</button>
      </div>
    </div>
  );
}

const Stat = ({ label, value, color }) => (
  <div style={{ ...cardStyle, padding: 14, borderLeft: `4px solid ${color}` }}>
    <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
  </div>
);

const btn = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: color, color: '#fff', border: 'none',
  borderRadius: theme.radiusSm, cursor: 'pointer', fontSize: 13, fontWeight: 600,
});

const pill = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '2px 8px', background: color + '20', color, borderRadius: 12,
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
});

const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
